import { useId } from "react";
import BranchPicker from "./BranchPicker";
import PrivacyToggle from "./PrivacyToggle";
import StrengthPicker from "./StrengthPicker";
import { blankFacet } from "../lib/facetForm";

export const RELATIONSHIP_OPTIONS = [
  { value: "friend", label: "Friend" },
  { value: "colleague", label: "Colleague" },
  { value: "alumni", label: "Alumni" },
  { value: "family", label: "Family" },
  { value: "acquaintance", label: "Acquaintance" },
  { value: "other", label: "Other" },
];

// Kept as a component-level export for consumers constructing controlled state;
// the canonical defaults live in the pure facet form module.
export const emptyFacet = blankFacet;

/**
 * Controlled fields for one personal or professional facet.
 * Value shape: { relationshipType, strength, whereMet, privateNote,
 * lastInteracted, discoveryEnabled, branch }.
 */
export default function ContextFacetFields({
  context = "professional",
  value,
  onChange,
  networkNodes = [],
  currentUid,
  excludeIds,
  disabled = false,
  idPrefix,
}) {
  const generatedId = useId().replace(/:/g, "");
  const prefix = idPrefix || `facet-${context}-${generatedId}`;
  const defaults = blankFacet(context);
  const facet = { ...defaults, ...(value || {}) };
  facet.branch = { ...defaults.branch, ...(value?.branch || {}) };

  const update = (patch) => onChange?.({ ...facet, ...patch });
  const contextLabel = context === "personal" ? "Personal" : "Professional";
  const discoveryDescription = context === "personal"
    ? "Off by default. When enabled, this person may appear in introduction paths, but nobody else sees the Personal label."
    : "Allow this person to appear in extended-network search and introduction paths.";

  return (
    <div className="context-facet-fields" data-context={context}>
      <div className="form-field context-origin-field">
        <BranchPicker
          id={`${prefix}-origin`}
          label={`How are you connected in your ${contextLabel.toLowerCase()} life?`}
          candidates={networkNodes}
          value={facet.branch}
          onChange={(branch) => update({ branch })}
          currentUid={currentUid}
          excludeIds={excludeIds}
          disabled={disabled}
        />
        <p className="context-field-help">
          This origin only affects the {contextLabel.toLowerCase()} map.
        </p>
      </div>

      <div className="form-field">
        <label htmlFor={`${prefix}-relationship`}>Relationship type</label>
        <select
          id={`${prefix}-relationship`}
          value={facet.relationshipType}
          onChange={(event) => update({ relationshipType: event.target.value })}
          disabled={disabled}
        >
          {RELATIONSHIP_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <fieldset className="context-fieldset" disabled={disabled}>
        <legend>Connection strength</legend>
        <StrengthPicker
          value={facet.strength}
          onChange={(strength) => update({ strength })}
          disabled={disabled}
        />
      </fieldset>

      <div className="form-field">
        <label htmlFor={`${prefix}-where-met`}>Where did you meet? <span>(optional)</span></label>
        <input
          id={`${prefix}-where-met`}
          type="text"
          placeholder={context === "personal" ? "e.g. Through our neighborhood book club" : "e.g. At the alumni career panel"}
          value={facet.whereMet}
          onChange={(event) => update({ whereMet: event.target.value })}
          disabled={disabled}
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${prefix}-private-note`}>Private note <span>— only you can see this</span></label>
        <textarea
          id={`${prefix}-private-note`}
          placeholder="Add something useful to remember…"
          value={facet.privateNote}
          onChange={(event) => update({ privateNote: event.target.value })}
          disabled={disabled}
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${prefix}-last-interacted`}>Last interacted <span>(optional)</span></label>
        <input
          id={`${prefix}-last-interacted`}
          type="date"
          value={facet.lastInteracted}
          onChange={(event) => update({ lastInteracted: event.target.value })}
          disabled={disabled}
        />
      </div>

      <div className="context-discovery-control">
        <PrivacyToggle
          id={`${prefix}-discovery`}
          label="Include in extended network"
          description={discoveryDescription}
          value={facet.discoveryEnabled}
          onChange={(discoveryEnabled) => update({ discoveryEnabled })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
